import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Breadcrumbs, 
  Container, 
  Grid, 
  Link, 
  Typography,
  Button,
  Card,
  TextField
} from '@material-ui/core';
import ChevronRightIcon from '../../icons/ChevronRight';
import useSettings from '../../hooks/useSettings';
import { formationApi } from '../../api/formationApi';
import { questionnaireApi } from '../../api/questionnaireApi';
import Chart from 'react-apexcharts';
import { Autocomplete } from '@mui/material';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { motion } from 'framer-motion';

const AnimatedMeshBackground = () => (
    <Box sx={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
        overflow: 'hidden', zIndex: 0, opacity: 0.4,
        background: '#F8FAFC',
        pointerEvents: 'none'
    }}>
        <motion.div
            animate={{ x: [0, 120, -60, 0], y: [0, -120, 60, 0], scale: [1, 1.3, 0.8, 1] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', top: '-15%', left: '-10%', width: '45vw', height: '45vw', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(60px)' }}
        />
        <motion.div
            animate={{ x: [0, -100, 80, 0], y: [0, 100, -50, 0], scale: [1, 1.2, 0.9, 1] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            style={{ position: 'absolute', bottom: '-25%', right: '-10%', width: '55vw', height: '55vw', background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', filter: 'blur(60px)' }}
        />
    </Box>
);

const getCategory = (percentage) => {
  const p = parseFloat(percentage);
  if (p < 50) return 'APPRENTI';
  if (p <= 75) return 'INTERMEDIAIRE';
  if (p <= 90) return 'CONFIRME';
  return 'EXPERT';
};

const getExpectedProgression = (prePercentage) => {
  const p = parseFloat(prePercentage);
  if (p < 50) return 'Niveau intermédiaire, soit +30%';
  if (p <= 75) return 'Niveau confirmé soit +30%';
  if (p <= 90) return 'Niveau confirmé soit de +3 à +7%';
  return 'Niveau expert, soit de 0% à 3%';
};

const QuestionnaireProgression = () => {
  const { settings } = useSettings();
  const [formations, setFormations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedFormation, setSelectedFormation] = useState('All');
  const [selectedSession, setSelectedSession] = useState('All');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    avgPre: 0,
    avgPost: 0,
    progression: 0,
    countPre: 0,
    countPost: 0,
    questionsProgression: [],
    sessionsProgression: [],
    learnersProgression: [],
    availableQuestionnaires: []
  });
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('All');

  const loadFormations = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const data = await formationApi.getFormations(accessToken);
      setFormations(data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadQuestionnaires = useCallback(async (formationId) => {
    if (formationId === 'All') {
      setStats(prev => ({ ...prev, availableQuestionnaires: [] }));
      setSelectedQuestionnaire('All');
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const data = await questionnaireApi.getQuestionnairesByFormationId(formationId, accessToken);
      setStats(prev => ({ ...prev, availableQuestionnaires: data || [] }));
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadSessions = useCallback(async (formationId) => {
    if (formationId === 'All') {
      setSessions([]);
      setSelectedSession('All');
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const data = await questionnaireApi.getSessionsByFormationId(formationId, accessToken);
      setSessions(data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadFormations();
  }, [loadFormations]);

  const handleFormationChange = (e) => {
    const value = e.target.value;
    setSelectedFormation(value);
    setSelectedSession('All');
    setSelectedQuestionnaire('All');
    loadSessions(value);
    loadQuestionnaires(value);
  };
  const loadProgressionData = useCallback(async () => {
    if (selectedFormation === 'All') return;
    
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      // Fetch Pre-Formation
      const resPre = await questionnaireApi.fetchSubmissionsByFilter('Pré-Formation', selectedFormation, selectedSession, accessToken);
      // Fetch Post-Formation
      const resPost = await questionnaireApi.fetchSubmissionsByFilter('Post-Formation', selectedFormation, selectedSession, accessToken);
      
      const calcAvg = (subs) => {
        if (!subs || subs.length === 0) return 0;
        let totalScore = 0;
        let maxPossible = 0;
        subs.forEach(s => {
          totalScore += parseFloat(s.score || 0);
          maxPossible += parseInt(s.total_points || 0);
        });
        return maxPossible > 0 ? (totalScore / maxPossible) * 100 : 0;
      };

      const avgPre = calcAvg(resPre.data);
      const avgPost = calcAvg(resPost.data);
      
      let progression = 0;
      if (avgPre > 0) {
        progression = ((avgPost - avgPre) / avgPre) * 100;
      } else if (avgPost > 0) {
        progression = 100;
      }

      // Calcul par question
      const allQuestionTexts = Array.from(new Set([
        ...(resPre.questions || []).map(q => q.text?.trim()),
        ...(resPost.questions || []).map(q => q.text?.trim())
      ])).filter(t => t);

      const questionsProgression = allQuestionTexts.map(text => {
        // Filtre par questionnaire si sélectionné
        const qMetadataPreAll = (resPre.questions || []).filter(q => q.text?.trim().toLowerCase() === text.toLowerCase());
        const qMetadataPostAll = (resPost.questions || []).filter(q => q.text?.trim().toLowerCase() === text.toLowerCase());
        
        let qMetadataPre = qMetadataPreAll[0];
        let qMetadataPost = qMetadataPostAll[0];

        if (selectedQuestionnaire !== 'All') {
            qMetadataPre = qMetadataPreAll.find(q => q.questionnaire_id.toString() === selectedQuestionnaire);
            qMetadataPost = qMetadataPostAll.find(q => q.questionnaire_id.toString() === selectedQuestionnaire);
        }

        // Si on filtre par questionnaire et que la question n'en fait pas partie, on l'ignore
        if (selectedQuestionnaire !== 'All' && !qMetadataPre && !qMetadataPost) return null;

        const points = parseFloat(qMetadataPre?.points || qMetadataPost?.points || 0);

        // Si la question n'a pas de points, on l'ignore pour l'affichage de progression
        if (points === 0) return null;

        const qAvgPre = calcQuestionAvg(resPre.data, resPre.questions, text, selectedQuestionnaire);
        const qAvgPost = calcQuestionAvg(resPost.data, resPost.questions, text, selectedQuestionnaire);
        
        let qProg = 0;
        if (qAvgPre > 0) {
          qProg = ((qAvgPost - qAvgPre) / qAvgPre) * 100;
        } else if (qAvgPost > 0) {
          qProg = 100;
        }

        return {
          text,
          avgPre: qAvgPre.toFixed(2),
          avgPost: qAvgPost.toFixed(2),
          progression: qProg.toFixed(1)
        };
      }).filter(q => q !== null);

      // Calcul par session (si session === 'All')
      let sessionsProgression = [];
      if (selectedSession === 'All') {
        const allSessionIds = Array.from(new Set([
          ...(resPre.data || []).map(s => s.session_id),
          ...(resPost.data || []).map(s => s.session_id)
        ])).filter(id => id !== null);

        sessionsProgression = allSessionIds.map(sid => {
          const sSubsPre = (resPre.data || []).filter(s => s.session_id === sid);
          const sSubsPost = (resPost.data || []).filter(s => s.session_id === sid);
          
          const sAvgPre = calcAvg(sSubsPre);
          const sAvgPost = calcAvg(sSubsPost);
          
          let sProg = 0;
          if (sAvgPre > 0) {
            sProg = ((sAvgPost - sAvgPre) / sAvgPre) * 100;
          } else if (sAvgPost > 0) {
            sProg = 100;
          }

          const sName = sSubsPre[0]?.session_name || sSubsPost[0]?.session_name || `Session ${sid}`;

          return {
            id: sid,
            name: sName,
            avgPre: sAvgPre.toFixed(2),
            avgPost: sAvgPost.toFixed(2),
            progression: sProg.toFixed(1),
            count: sSubsPre.length + sSubsPost.length
          };
        }).filter(s => s.count > 0).sort((a, b) => b.progression - a.progression);
      }

      // Calcul par apprenant (si session spécifiée)
      let learnersProgression = [];
      if (selectedSession !== 'All') {
        const allLearnerIds = Array.from(new Set([
          ...(resPre.data || []).map(s => s.customer_id),
          ...(resPost.data || []).map(s => s.customer_id)
        ])).filter(id => id !== null);

        learnersProgression = allLearnerIds.map(cid => {
          const lSubsPre = (resPre.data || []).filter(s => s.customer_id === cid);
          const lSubsPost = (resPost.data || []).filter(s => s.customer_id === cid);
          
          const lAvgPre = calcAvg(lSubsPre);
          const lAvgPost = calcAvg(lSubsPost);
          
          const name = lSubsPre[0] ? `${lSubsPre[0].firstname} ${lSubsPre[0].lastname[0]}.` : 
                       (lSubsPost[0] ? `${lSubsPost[0].firstname} ${lSubsPost[0].lastname[0]}.` : `Apprenant ${cid}`);

          return {
            id: cid,
            name,
            avgPre: parseFloat(lAvgPre.toFixed(2)),
            avgPost: parseFloat(lAvgPost.toFixed(2))
          };
        });
      }

      setStats(prev => ({
        ...prev,
        avgPre: parseFloat(avgPre).toFixed(2),
        avgPost: parseFloat(avgPost).toFixed(2),
        progression: parseFloat(progression).toFixed(1),
        countPre: resPre.data?.length || 0,
        countPost: resPost.data?.length || 0,
        questionsProgression,
        sessionsProgression,
        learnersProgression
      }));

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedFormation, selectedSession, selectedQuestionnaire]);

  const calcQuestionAvg = (subs, questions, text, questionnaireId = 'All') => {
    if (!subs || !questions) return 0;
    
    // On trouve toutes les versions de cette question (même texte)
    let targetQuestions = questions.filter(q => q.text?.trim().toLowerCase() === text.toLowerCase());
    
    if (questionnaireId !== 'All') {
        targetQuestions = targetQuestions.filter(q => q.questionnaire_id.toString() === questionnaireId);
    }
    
    let sum = 0;
    let count = 0;
    subs.forEach(s => {
        const answers = typeof s.answers === 'string' ? JSON.parse(s.answers) : s.answers;
        if (!answers) return;

        // Trouver la question spécifique à ce questionnaire
        const qMetadata = targetQuestions.find(q => q.questionnaire_id === s.questionnaire_id);
        if (qMetadata) {
            const qid = qMetadata.id.toString();
            if (answers[qid] !== undefined) {
                let score = 0;
                const answerVal = answers[qid]?.toString().trim();
                const points = parseFloat(qMetadata.points || 0);
                
                // On ne traite comme score que si c'est un nombre pur (pas une date ou du texte commençant par un chiffre)
                const isPureNumeric = /^\d+(\.\d+)?$/.test(answerVal);

                // Si une bonne réponse est définie, on compare (système de points)
                if (qMetadata.correct_answer && qMetadata.correct_answer.trim() !== '') {
                    // Nettoyage robuste : enlever puces (•), espaces superflus en début/fin de chaque fragment
                    const cleanFragment = (str) => str.replace(/^[\s•\-*]+/, '').replace(/[\s]+$/, '').toLowerCase().trim();
                    
                    const parseAnswersSafe = (val) => {
                        if (!val) return [];
                        try {
                            const parsed = JSON.parse(val);
                            return Array.isArray(parsed) ? parsed.map(String) : [String(val)];
                        } catch (e) {
                            return val.toString().split(',');
                        }
                    };
                    
                    const learnerFragments = parseAnswersSafe(answerVal).map(cleanFragment).filter(Boolean).sort();
                    const correctFragments = parseAnswersSafe(qMetadata.correct_answer).map(cleanFragment).filter(Boolean).sort();
                    
                    if (JSON.stringify(learnerFragments) === JSON.stringify(correctFragments)) {
                        score = points;
                    }
                } 
                // Sinon, si c'est une valeur numérique pure et que la question a des points ou est de type évaluation
                else if (isPureNumeric && points > 0) {
                    score = parseFloat(answerVal);
                }
                
                sum += points > 0 ? (score / points) * 100 : 0;
                count++;
            }
        }
    });
    return count > 0 ? (sum / count) : 0;
  };

  useEffect(() => {
    loadProgressionData();
  }, [loadProgressionData]);

  return (
    <>
      <Helmet>
        <title>Dashboard: Visualisation Pré/Post | Apteed</title>
      </Helmet>
      <Box
        sx={{
          bgcolor: '#F8FAFC',
          minHeight: '100%',
          position: 'relative',
          overflow: 'hidden',
          pb: 10,
          pt: 4
        }}
      >
        <AnimatedMeshBackground />

        <Container maxWidth={settings.compact ? 'xl' : false} sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard/questionnaire-satisfaction"
              startIcon={<ArrowLeftIcon style={{ fontSize: 14 }} />}
              size="small"
              sx={{
                color: '#64748B',
                fontWeight: 800,
                textTransform: 'none',
                bgcolor: 'rgba(0,0,0,0.03)',
                borderRadius: '10px',
                px: 2,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
              }}
            >
              Retour
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Box sx={{ 
                  p: 3, borderRadius: '24px', 
                  background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.9)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  display: 'flex', alignItems: 'center', gap: 3
              }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
                    flexShrink: 0,
                  }}
                >
                  <TrendingUpIcon sx={{ color: '#fff', fontSize: '1.7rem' }} />
                </Box>
                <Box>
                  <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1 }}>
                      <Link color="textPrimary" component={RouterLink} to="/dashboard" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: '#6366f1' } }}>Dashboard</Link>
                      <Link color="textPrimary" component={RouterLink} to="/dashboard/questionnaire-satisfaction" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', '&:hover': { color: '#6366f1' } }}>Résultats Qualité</Link>
                      <Typography color="textSecondary" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Progression</Typography>
                  </Breadcrumbs>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: '#0F172A', letterSpacing: '-0.02em', mb: 0.5 }}>
                      Progression Pré/Post Formation
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                      Visualisation et analyse comparatives des évaluations initiales et finales.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          
          <Card sx={{ p: 2, mt: 4, borderRadius: '16px' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                    fullWidth
                    options={[{ id: 'All', name: 'Toutes les formations', programNumberAll: '' }, ...formations]}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      if (option.id === 'All') return "Toutes les formations";
                      return `${option.name} ${option.programNumberAll ? `(N° ${option.programNumberAll})` : ''}`.trim();
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={
                        selectedFormation === 'All'
                            ? null
                            : formations.find(f => f.id === selectedFormation) || null
                    }
                    onChange={(event, newValue) => {
                        handleFormationChange({ target: { value: newValue ? newValue.id : 'All' } });
                    }}
                    renderInput={(params) => (
                        <TextField 
                            {...params} 
                            label="Filtrer par Formation" 
                            variant="outlined" 
                            size="small" 
                            InputProps={{ ...params.InputProps, style: { borderRadius: '12px', backgroundColor: 'white' } }} 
                        />
                    )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Filtrer par Session"
                  name="session"
                  onChange={(e) => setSelectedSession(e.target.value)}
                  select
                  SelectProps={{ native: true }}
                  value={selectedSession}
                  variant="outlined"
                  size="small"
                  disabled={selectedFormation === 'All'}
                >
                  <option value="All">Toutes les sessions</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      Session N° {s.number}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Card>
          
          <Box sx={{ mt: 4 }}>
            {selectedFormation === 'All' ? (
              <Card sx={{ p: 4, textAlign: 'center', borderRadius: '16px', border: '1px dashed #E2E8F0', bgcolor: '#F8FAFC' }}>
                <Typography variant="h6" color="textSecondary">
                    Veuillez sélectionner une formation pour voir la progression.
                </Typography>
              </Card>
            ) : (
              <Grid container spacing={4} alignItems="center" justifyContent="center">
                {/* Bloc Pré-Formation */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    p: 2.5, 
                    textAlign: 'center', 
                    borderRadius: '20px', 
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    border: '1px solid #E2E8F0',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '4px', bgcolor: '#6366F1' }} />
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#6366F1', letterSpacing: '1px', fontSize: '0.65rem' }}>
                      Évaluation Initiale
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 800, color: '#1E293B' }}>
                      Pré-Formation
                    </Typography>
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h3" sx={{ fontWeight: 900, color: '#6366F1' }}>
                        {stats.avgPre}<span style={{ fontSize: '1.2rem', opacity: 0.6 }}>%</span>
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#6366F1' }}>
                        ({getCategory(stats.avgPre)})
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      <strong>{stats.countPre}</strong> réponses
                    </Typography>
                  </Card>
                </Grid>

                {/* Flèche de Progression */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ 
                      display: 'inline-flex', 
                      flexDirection: 'column', 
                      alignItems: 'center'
                    }}>
                      <Typography variant="h5" sx={{ 
                        color: stats.progression >= 0 ? '#10B981' : '#EF4444', 
                        fontWeight: 800,
                        mb: 0.5
                      }}>
                        {stats.progression >= 0 ? '+' : ''}{stats.progression}%
                      </Typography>
                      
                      <Box sx={{ 
                        width: '80px', 
                        height: '30px', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="60" height="24" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 20H95M95 20L80 5M95 20L80 35" stroke={stats.progression >= 0 ? '#10B981' : '#EF4444'} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </Box>

                      <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.6rem' }}>
                        {getCategory(stats.avgPre)} → {getCategory(stats.avgPost)}
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 1, fontWeight: 600, color: '#6366F1', fontSize: '0.65rem', display: 'block', bgcolor: 'rgba(99, 102, 241, 0.05)', p: 0.5, borderRadius: 1 }}>
                        Objectif attendu: {getExpectedProgression(stats.avgPre)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Bloc Post-Formation */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    p: 2.5, 
                    textAlign: 'center', 
                    borderRadius: '20px', 
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    border: '1px solid #E2E8F0',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '4px', bgcolor: '#10B981' }} />
                    <Typography variant="overline" sx={{ fontWeight: 800, color: '#10B981', letterSpacing: '1px', fontSize: '0.65rem' }}>
                      Évaluation Finale
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 800, color: '#1E293B' }}>
                      Post-Formation
                    </Typography>
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h3" sx={{ fontWeight: 900, color: '#10B981' }}>
                        {stats.avgPost}<span style={{ fontSize: '1.2rem', opacity: 0.6 }}>%</span>
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#10B981' }}>
                        ({getCategory(stats.avgPost)})
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      <strong>{stats.countPost}</strong> réponses
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Graphique de Progression */}
            {selectedFormation !== 'All' && (
              <Box sx={{ mt: 8 }}>
                <Typography variant="h6" sx={{ mb: 4, fontWeight: 800, color: '#1E293B', display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 4, height: 24, bgcolor: '#10B981', mr: 2, borderRadius: 2 }} />
                  {selectedSession === 'All' ? 'Progression par Session' : 'Progression par Apprenant'}
                </Typography>
                
                <Card sx={{ p: 3, borderRadius: '16px', border: '1px solid #F1F5F9' }}>
                    <Chart
                        type="bar"
                        height={400}
                        series={[
                            {
                                name: 'Pré-formation',
                                data: selectedSession === 'All' 
                                    ? stats.sessionsProgression.map(s => s.avgPre) 
                                    : stats.learnersProgression.map(l => l.avgPre)
                            },
                            {
                                name: 'Post-formation',
                                data: selectedSession === 'All' 
                                    ? stats.sessionsProgression.map(s => s.avgPost) 
                                    : stats.learnersProgression.map(l => l.avgPost)
                            }
                        ]}
                        options={{
                            chart: {
                                toolbar: { show: false }
                            },
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                    columnWidth: '50%',
                                    borderRadius: 4,
                                    dataLabels: { position: 'top' }
                                }
                            },
                            dataLabels: {
                                enabled: true,
                                style: { fontSize: '10px', colors: ['#1E293B'] },
                                offsetY: -20
                            },
                            colors: ['#BAE6FD', '#0C4A6E'],
                            xaxis: {
                                categories: selectedSession === 'All' 
                                    ? stats.sessionsProgression.map(s => `Session ${s.name}`) 
                                    : stats.learnersProgression.map(l => l.name)
                            },
                            yaxis: {
                                max: 100,
                                title: { text: 'Note en %' }
                            },
                            legend: { position: 'top' }
                        }}
                    />
                </Card>
              </Box>
            )}

            {/* Détail par question (Graphique Radar si All Sessions, Cartes si Session spécifique) */}
            {selectedFormation !== 'All' && stats.questionsProgression.length > 0 && (
              <Box sx={{ mt: 8 }}>
                {selectedSession === 'All' ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: 4, height: 24, bgcolor: '#6366F1', mr: 2, borderRadius: 2 }} />
                            Analyse comparative par Question
                        </Typography>
                        
                        <TextField
                            select
                            SelectProps={{ native: true }}
                            value={selectedQuestionnaire}
                            onChange={(e) => setSelectedQuestionnaire(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ width: 300 }}
                            label="Filtrer par Questionnaire"
                        >
                            <option value="All">Tous les questionnaires</option>
                            {stats.availableQuestionnaires.map((q) => (
                                <option key={q.id} value={q.id}>
                                    {q.name} ({q.type})
                                </option>
                            ))}
                        </TextField>
                    </Box>
                    
                    <Card sx={{ p: 3, borderRadius: '16px', border: '1px solid #F1F5F9' }}>
                        <Chart
                            type="radar"
                            height={500}
                            series={[
                                {
                                    name: 'Pré-formation',
                                    data: stats.questionsProgression.map(q => q.avgPre)
                                },
                                {
                                    name: 'Post-formation',
                                    data: stats.questionsProgression.map(q => q.avgPost)
                                }
                            ]}
                            options={{
                                chart: {
                                    dropShadow: { enabled: true, blur: 1, left: 1, top: 1 }
                                },
                                colors: ['#6366F1', '#10B981'],
                                stroke: { width: 2 },
                                fill: { opacity: 0.2 },
                                markers: { size: 4 },
                                xaxis: {
                                    categories: stats.questionsProgression.map(q => q.text.length > 30 ? q.text.substring(0, 30) + '...' : q.text)
                                },
                                yaxis: {
                                    show: true,
                                    max: 100,
                                    tickAmount: 4
                                },
                                legend: { position: 'bottom' }
                            }}
                        />
                    </Card>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" sx={{ mb: 4, fontWeight: 800, color: '#1E293B', display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 4, height: 24, bgcolor: '#6366F1', mr: 2, borderRadius: 2 }} />
                      Détail de progression par question
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {stats.questionsProgression.map((q, idx) => (
                        <Grid item xs={12} key={idx}>
                          <Card sx={{ 
                            p: 2, 
                            borderRadius: '16px', 
                            border: '1px solid #F1F5F9',
                            '&:hover': { boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }
                          }}>
                            <Grid container alignItems="center" spacing={2}>
                              <Grid item xs={12} md={5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569' }}>
                                  {q.text}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={4} md={2} sx={{ textAlign: 'center' }}>
                                <Box sx={{ bgcolor: '#F1F5F9', p: 1, borderRadius: '12px' }}>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontWeight: 600 }}>PRÉ</Typography>
                                  <Typography variant="h6" sx={{ color: '#6366F1', fontWeight: 800, lineHeight: 1.2 }}>{q.avgPre}%</Typography>
                                  <Typography variant="caption" sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.55rem' }}>({getCategory(q.avgPre)})</Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={4} md={3} sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Typography variant="caption" sx={{ 
                                    fontWeight: 800, 
                                    color: q.progression >= 0 ? '#10B981' : '#EF4444' 
                                  }}>
                                    {q.progression >= 0 ? '+' : ''}{q.progression}%
                                  </Typography>
                                  <svg width="40" height="16" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: q.progression < 0 ? 'scaleX(-1)' : 'none' }}>
                                    <path d="M5 20H95M95 20L80 5M95 20L80 35" stroke={q.progression >= 0 ? '#10B981' : '#EF4444'} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </Box>
                              </Grid>

                              <Grid item xs={4} md={2} sx={{ textAlign: 'center' }}>
                                <Box sx={{ bgcolor: '#F1F5F9', p: 1, borderRadius: '12px' }}>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontWeight: 600 }}>POST</Typography>
                                  <Typography variant="h6" sx={{ color: '#10B981', fontWeight: 800, lineHeight: 1.2 }}>{q.avgPost}%</Typography>
                                  <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700, fontSize: '0.55rem' }}>({getCategory(q.avgPost)})</Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default QuestionnaireProgression;
